import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { FaqClusterProps } from '@/isomorphic/components/wp-blocks/faq-cluster/faq-cluster';
import { AppContext } from '@/contexts/app-context';
import { useLocalization } from '@/hooks/use-localization';
import { InsightProps } from '@/isomorphic/components/wp-blocks/insights/insight';
import { useBeforeMount } from '@24hr/react-hooks';
import { getSupremeCards, SUPREME_CARDS_QUERY_KEY } from '@/queries/supreme-cards';
import { useSupremeCards } from '@/hooks/use-supreme-cards';
import { getImageUrl } from '@/utils/images';
import logoUrl from '@/assets/logos/resurs_logo_rgb_black.svg';

type SchemaType = 'BankOrCreditUnion' | 'CreditCard' | 'LoanOrCredit' | 'PaymentService' | 'DepositAccount';

type SchemaBlockProps = {
    schemaType: SchemaType;
    creditCard?: null | {
        id: number;
        title: string;
        slug: string;
    };
    loanOrCredit?: null | {
        name: string;
        minValue: string | number;
        maxValue: string | number;
        percentageMinValue: string | number;
        percentageMaxValue: string | number;
    };
    paymentService?: null | {
        name: string;
        description: string;
    };
    depositAccount?: null | {
        name: string;
        description: string;
        termsOfService: string;
        interestRate: string | number;
    };
};

export const useSchema = () => {
    const context = useContext(AppContext);

    const { pathname } = useLocation();
    const { supremeCards } = useSupremeCards();
    const { getCurrencyCode } = useLocalization();
    const { pageQuery, findAllBlocksOfType, getSetting } = useGlobalQueries();

    useBeforeMount(() => {
        context.prefetch({ queryKey: [SUPREME_CARDS_QUERY_KEY], queryFn: () => getSupremeCards(context) });
    });

    const faqBlocks = useMemo(() => findAllBlocksOfType<FaqClusterProps>('resurs/faq-cluster').flatMap(block => block.questions), [pageQuery.data]);
    const insightBlocks = useMemo(() => findAllBlocksOfType<InsightProps>('resurs/resursbanken-post'), [pageQuery.data]);
    const schemaBlocks = useMemo(() => findAllBlocksOfType<SchemaBlockProps>('resurs/schema'), [pageQuery.data]);

    function genericSchema() {
        if (!schemaBlocks?.length) {
            return [];
        }

        return schemaBlocks.map(block => {
            switch (block.schemaType) {
                case 'BankOrCreditUnion':
                    return brokerSchema();
                case 'CreditCard':
                    return creditCardSchema(block);
                case 'LoanOrCredit':
                    return loanCreditSchema(block);
                case 'PaymentService':
                    return paymentServiceSchema(block);
                case 'DepositAccount':
                    return depositAccountSchema(block);
            }
        });
    }

    function getArea() {
        switch (context.country) {
            case 'se':
                return 'Sverige';
            case 'no':
                return 'Norge';
            case 'dk':
                return 'Danmark';
            case 'fi':
                return 'Suomi';
        }
    }

    function getProvider() {
        return {
            '@type': 'BankOrCreditUnion',
            name: 'Resurs Bank',
            url: context.url,
        };
    }

    function brokerSchema(nested = false) {
        const schema = getSetting('schema_fields');
        if (!schema) {
            console.error('SCHEMA DATA MISSING! Please check the general settings panel in the CMS.');
            return {
                ERROR: 'Schema data missing',
            };
        }

        const socialLinks = schema?.social_links?.length ? schema.social_links : [];
        const returnObj = {
            '@type': 'BankOrCreditUnion',
            name: 'Resurs Bank',
            url: context.url,
            logo: `${context.url}${logoUrl}`,
            telephone: schema?.phone || '',
            email: schema?.email || '',
            address: {
                '@type': 'PostalAddress',
                streetAddress: schema?.street_address || '',
                addressLocality: schema?.locality || '',
                postalCode: schema?.post_code || '',
                addressCountry: `${schema?.address_country?.trim() || context.country}`.toLocaleUpperCase(),
            },
            ...(socialLinks.length ? { sameAs: socialLinks.map(link => link.url) } : {}),
            foundingDate: schema?.founding_date || '1977',
            areaServed: getArea(),
        };

        return {
            ...returnObj,
            ...(nested ? {} : { '@context': 'https://schema.org' }),
        };
    }

    function creditCardSchema(block: SchemaBlockProps) {
        if (!block.creditCard?.id) {
            return null;
        }

        const card = supremeCards?.data?.find(sc => `${sc.id}` === `${block.creditCard?.id}`);
        if (!card) {
            return null;
        }

        const schema: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@type': 'CreditCard',
            name: card.fullname || 'Resurs Bank Kreditkort',
            url: `${context.url}${pathname}`,
            provider: getProvider(),
        };

        if (card.documents?.general_terms?.url) {
            schema.termsOfService = card.documents.general_terms.url;
        }

        // Need to decide how to implement this with Resurs...
        // offers: {
        //     '@type': 'Offer',
        //     priceCurrency: getCurrencyCode(),
        //     price: '0',
        //     availability: 'http://schema.org/InStock',
        // },

        return schema;
    }

    function loanCreditSchema(block: SchemaBlockProps) {
        if (!block?.loanOrCredit) {
            return null;
        }

        const schema: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@type': 'LoanOrCredit',
            name: block.loanOrCredit?.name || '',
            provider: brokerSchema(true),
        };

        if (block.loanOrCredit?.minValue && block.loanOrCredit?.maxValue) {
            schema.amount = {
                '@type': 'MonetaryAmount',
                currency: getCurrencyCode(),
                minValue: parseInt(`${block.loanOrCredit?.minValue}`),
                maxValue: parseInt(`${block.loanOrCredit?.maxValue}`),
            };
        }

        if (block.loanOrCredit?.percentageMinValue && block.loanOrCredit?.percentageMaxValue) {
            schema.interestRate = {
                '@type': 'QuantitativeValue',
                minValue: block.loanOrCredit?.percentageMinValue,
                maxValue: block.loanOrCredit?.percentageMaxValue,
            };
        }

        return schema;
    }

    function paymentServiceSchema(block: SchemaBlockProps) {
        return {
            '@context': 'https://schema.org',
            '@type': 'PaymentService',
            name: block?.paymentService?.name || '',
            description: block?.paymentService?.description || '',
            serviceType: 'Payment Solutions',
            url: `${context.url}${pathname}`,
            provider: getProvider(),
        };

        // Need to decide how to implement this with Resurs...
        // offers: {
        //     '@type': 'Offer',
        //     currency: getCurrencyCode(),
        //     price: '0',
        //     availability: 'http://schema.org/InStock',
        // },
    }

    function depositAccountSchema(block: SchemaBlockProps) {
        const schema: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@type': 'DepositAccount',
            name: block?.depositAccount?.name || '',
            description: block?.depositAccount?.description || '',
            url: `${context.url}${pathname}`,
            provider: getProvider(),
        };

        if (block?.depositAccount?.interestRate) {
            schema.interestRate = {
                '@type': 'QuantitativeValue',
                value: block.depositAccount.interestRate,
                unitText: 'percent',
            };
        }

        if (block?.depositAccount?.termsOfService) {
            schema.termsOfService = block?.depositAccount?.termsOfService;
        }

        // Need to decide how to implement this with Resurs...
        // offers: {
        //     '@type': 'Offer',
        //     priceCurrency: 'SEK',
        //     price: '0',
        //     availability: 'http://schema.org/InStock',
        // },

        return schema;
    }

    function faqSchema() {
        if (!faqBlocks.length) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqBlocks.map(({ question, answer }) => ({
                '@type': 'Question',
                name: question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: answer,
                },
            })),
        };
    }

    function articleSchema() {
        if (!insightBlocks?.length) {
            return null;
        }

        const insight = insightBlocks[0];
        const post = insight?.attributes?.transformAttributes?.post;
        if (!post) {
            return null;
        }

        const data: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@type': 'Article',
            url: `${context.url}${pathname}`,
            headline: post.page_title,
            datePublished: post.post_date,
            publisher: {
                '@type': 'BankOrCreditUnion',
                name: 'Resurs Bank',
                url: context.url,
            },
        };

        if (post.post_revision_date) {
            data.dateModified = post.post_revision_date;
        }

        if (post?.main_image?.src) {
            const imgUUrl = getImageUrl(post.main_image.src);
            data.image = `${context.url}${imgUUrl}`;
        }

        return data;
    }

    const getSchemas = () => [...genericSchema(), faqSchema(), articleSchema()].filter(Boolean);

    return {
        getSchemas,
    };
};
